// Use xlsx-js-style for full styling support
import * as xlsx from 'xlsx-js-style'
import { EXCEL_CELL_STYLES } from './excelStyles'
import { VerificationSheetData } from './verificationSheetAggregator'

/**
 * Excel生成時の制限値
 */
const EXCEL_MAX_DETAIL_ROWS_LIMITS = 60000 // 詳細データの最大行数（安全マージン含む）

/**
 * 検証シートデータの検証
 */
const validateVerificationSheetData = (
  data: VerificationSheetData
): boolean => {
  if (!data) {
    return false
  }

  // 大量データの制限チェック
  if (data.detailRows.length > EXCEL_MAX_DETAIL_ROWS_LIMITS) {
    return false
  }

  return true
}

/**
 * Excel生成の設定定数
 */
const EXCEL_CONSTANTS = {
  // 行・列の位置定数
  LAYOUT: {
    HEADER_ROW_INDEX: 7, // 統計情報の後のヘッダー行のインデックス（0ベース）
    TOTAL_COLUMNS: 11, // 列数
    STATS_SECTION_END: 6, // 統計セクションの終了行インデックス
  },
  // 行範囲定数
  ROWS: {
    STATS_HEADER: 0, // 統計ヘッダー行
    STATS_DATA: 1, // 統計データ行
    EMPTY_LINE_1: 2, // 空行1
    TOTALS_HEADER: 3, // 合計値ヘッダー行
    NET_SALES_TOTAL: 4, // 純売上合計行
    OTHER_DEDUCTIONS_TOTAL: 5, // その他控除合計行
    EMPTY_LINE_2: 6, // 空行2
    DATA_HEADER: 7, // データヘッダー行
  },
}

/**
 * ラベル定数
 */
const EXCEL_LABELS = {
  REPORTED_TENANT_COUNT: '報告店舗数',
  REQUIRED_TO_REPORT_COUNT: '報告必要店舗数',
  SALES_ITEM_HEADER: '売上項目',
  TOTAL_CONFIRMED_VALUE_HEADER: '合計(確定値)',
  NET_SALES_TOTAL: '純売上',
  OTHER_DEDUCTIONS_TOTAL: 'その他控除',
  HEADERS: {
    SALES_DATE: '売上日',
    STORE_CODE: '店舗コード',
    STORE_NAME: '店舗名',
    TENANT_CODE: 'テナントコード',
    TENANT_NAME: 'テナント名',
    ITEM: '項目',
    OCR_VALUE: 'OCR値',
    MANUAL_INPUT: '手入力値',
    DEFINITE_VALUE: '確定値',
    TENANT_MESSAGE: 'テナントからの補足事項',
    ADMIN_MEMO: '管理者メモ\n（報告ごと）',
  },
  DEFAULT_SHEET_NAME: 'Sheet1', // TODO: シート名は要検討
}

/**
 * 列設定統合（幅、ヘッダー、スタイル）
 */
const COLUMN_CONFIG = [
  {
    key: 'SALES_DATE',
    width: 12,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'RIGHT_ALIGNED_TEXT',
  },
  {
    key: 'STORE_CODE',
    width: 12,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'RIGHT_ALIGNED_TEXT',
  },
  {
    key: 'STORE_NAME',
    width: 20,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'LEFT_ALIGNED_TEXT',
  },
  {
    key: 'TENANT_CODE',
    width: 15,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'RIGHT_ALIGNED_TEXT',
  },
  {
    key: 'TENANT_NAME',
    width: 25,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'LEFT_ALIGNED_TEXT',
  },
  {
    key: 'ITEM',
    width: 15,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'DATA_CELL',
  },
  {
    key: 'OCR_VALUE',
    width: 12,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'NUMERIC_CELL',
  },
  {
    key: 'MANUAL_INPUT',
    width: 12,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'NUMERIC_CELL',
  },
  {
    key: 'DEFINITE_VALUE',
    width: 12,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'NUMERIC_CELL',
  },
  {
    key: 'TENANT_MESSAGE',
    width: 25,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'LEFT_ALIGNED_TEXT',
  },
  {
    key: 'ADMIN_MEMO',
    width: 20,
    headerStyle: 'TENANT_DETAIL_HEADER',
    dataStyle: 'LEFT_ALIGNED_TEXT',
  },
]

/**
 * Excel生成結果
 */
export interface ExcelGenerationResult {
  workbook: xlsx.WorkBook
  success: boolean
}

/**
 * セル値の型定義
 */
type CellValue = string | number | null

/**
 * 統計情報テーブルを構築
 */
const buildStatsTable = (data: VerificationSheetData): CellValue[][] => {
  return [
    // 1-2行目：テナント統計情報
    [EXCEL_LABELS.REPORTED_TENANT_COUNT, EXCEL_LABELS.REQUIRED_TO_REPORT_COUNT],
    [
      data.tenantStats.reportedTenantCount,
      data.tenantStats.requiredToReportCount,
    ],
    // 3行目：空行
    [],
    // 4行目：売上項目ヘッダー
    [EXCEL_LABELS.SALES_ITEM_HEADER, EXCEL_LABELS.TOTAL_CONFIRMED_VALUE_HEADER],
    // 5-6行目：売上合計データ
    [EXCEL_LABELS.OTHER_DEDUCTIONS_TOTAL, data.totals.otherDeductions],
    [EXCEL_LABELS.NET_SALES_TOTAL, data.totals.netSales],
    // 7行目：空行
    [],
  ]
}

/**
 * 詳細データテーブルを構築
 */
const buildDetailTable = (data: VerificationSheetData): CellValue[][] => {
  const headerRow: CellValue[] = Object.values(EXCEL_LABELS.HEADERS)

  // データ行（パフォーマンス最適化：mapを使用）
  const dataRows = data.detailRows.map((row): CellValue[] => [
    row.salesDate.replace(/-/g, '/'),
    row.storeCode,
    row.storeName,
    row.tenantCode,
    row.tenantName,
    row.item,
    row.ocrValue,
    row.manualInputValue,
    row.definiteValue,
    row.tenantMessage ?? '',
    row.adminMemo ?? '',
  ])

  return [headerRow, ...dataRows]
}

/**
 * ワークシートデータを構築（2次元配列形式）
 */
const buildWorksheetData = (data: VerificationSheetData): CellValue[][] => {
  return [...buildStatsTable(data), ...buildDetailTable(data)]
}

/**
 * セルにスタイルを適用 - 直接変更版（パフォーマンス最適化）
 * Object.assignによる全ワークシートコピーを除去し、セルの直接変更方式に変更
 *
 * @param worksheet - 変更対象のワークシート（このパラメータは直接変更されます）
 * @param cellAddress - スタイルを適用するセルのアドレス
 * @param style - 適用するスタイル定義
 * @mutates worksheet - このメソッドは渡されたworksheetオブジェクトを直接変更します
 */
const mutateCellStyle = (
  worksheet: xlsx.WorkSheet,
  cellAddress: string,
  style: xlsx.CellStyle
): void => {
  // セルが存在しない場合は作成
  if (!worksheet[cellAddress]) {
    /* eslint-disable-next-line no-param-reassign */
    worksheet[cellAddress] = { t: 's', v: '' }
  }

  // セルのスタイルプロパティを直接設定（中間オブジェクト生成を削減）
  // 注意: この関数はworksheetパラメータを意図的に変更します（関数名が示す通り）
  const cell = worksheet[cellAddress]
  const existingStyle = cell.s || {}

  // スタイルを直接マージして適用（不要なオブジェクトコピーを削減）
  // ワークシートのセルを直接変更することでパフォーマンスを最適化
  /* eslint-disable-next-line no-param-reassign */
  cell.s = {
    ...existingStyle,
    ...style,
  }
}

/**
 * セル配列型定義（バッチ処理用）
 */
interface CellStyleBatch {
  row: number
  col: number
  style: xlsx.CellStyle
}

/**
 * セルアドレス計算のキャッシュ（パフォーマンス最適化）
 */
const encodeCell = (row: number, col: number): string =>
  xlsx.utils.encode_cell({ r: row, c: col })

/**
 * バッチスタイル適用（配列駆動式アプローチ）
 */
const applyBatchStyles = (
  worksheet: xlsx.WorkSheet,
  styleBatches: CellStyleBatch[]
): void => {
  // 1回のループで全スタイルを適用（計算量O(n)に削減）
  styleBatches.forEach(({ row, col, style }) => {
    mutateCellStyle(worksheet, encodeCell(row, col), style)
  })
}

/**
 * ワークシートにスタイルを適用 - パフォーマンス最適化版
 * 冗長な個別セル処理ループの統合と配列駆動式アプローチの採用
 */
const applyWorksheetStyles = (
  worksheet: xlsx.WorkSheet,
  worksheetData: CellValue[][]
): void => {
  const { HEADER_ROW_INDEX } = EXCEL_CONSTANTS.LAYOUT
  const totalRows = worksheetData.length
  const { ROWS } = EXCEL_CONSTANTS

  // 統計情報セクションのスタイル定義
  const statsStyles: CellStyleBatch[] = [
    // 1行目：統計ヘッダー
    { row: ROWS.STATS_HEADER, col: 0, style: EXCEL_CELL_STYLES.STATS_HEADER },
    {
      row: ROWS.STATS_HEADER,
      col: 1,
      style: EXCEL_CELL_STYLES.LIGHT_GREEN_HEADER,
    },
    // 2行目：統計データ値
    { row: ROWS.STATS_DATA, col: 0, style: EXCEL_CELL_STYLES.NUMERIC_CELL },
    { row: ROWS.STATS_DATA, col: 1, style: EXCEL_CELL_STYLES.NUMERIC_CELL },
    // 4行目：合計値ヘッダー
    { row: ROWS.TOTALS_HEADER, col: 0, style: EXCEL_CELL_STYLES.STATS_HEADER },
    { row: ROWS.TOTALS_HEADER, col: 1, style: EXCEL_CELL_STYLES.STATS_HEADER },
  ]

  // 5-6行目：合計値データのスタイル定義
  const totalsDataStyles: CellStyleBatch[] = []
  for (
    let row = ROWS.NET_SALES_TOTAL;
    row <= ROWS.OTHER_DEDUCTIONS_TOTAL;
    row++
  ) {
    totalsDataStyles.push(
      { row, col: 0, style: EXCEL_CELL_STYLES.DATA_CELL },
      { row, col: 1, style: EXCEL_CELL_STYLES.NUMERIC_CELL }
    )
  }

  // メインテーブルヘッダーのスタイル定義
  const headerStyles: CellStyleBatch[] = COLUMN_CONFIG.map(
    (colConfig, colIndex) => ({
      row: HEADER_ROW_INDEX,
      col: colIndex,
      style: EXCEL_CELL_STYLES[colConfig.headerStyle],
    })
  )

  // データ行のスタイル定義
  const dataStyles: CellStyleBatch[] = []
  for (let row = HEADER_ROW_INDEX + 1; row < totalRows; row++) {
    COLUMN_CONFIG.forEach((colConfig, colIndex) => {
      dataStyles.push({
        row,
        col: colIndex,
        style: EXCEL_CELL_STYLES[colConfig.dataStyle],
      })
    })
  }

  const styleBatches: CellStyleBatch[] = [
    ...statsStyles,
    ...totalsDataStyles,
    ...headerStyles,
    ...dataStyles,
  ]
  applyBatchStyles(worksheet, styleBatches)
}

/**
 * 検証シートデータをExcelワークブック形式に変換
 */
export const generateVerificationSheetExcel = (
  data: VerificationSheetData
): ExcelGenerationResult => {
  try {
    // 入力データの検証
    const isValid = validateVerificationSheetData(data)
    if (!isValid) {
      // エラーの場合は空のワークブックを返す
      const emptyWorkbook = xlsx.utils.book_new()
      const emptyWorksheet = xlsx.utils.aoa_to_sheet([[]])
      xlsx.utils.book_append_sheet(
        emptyWorkbook,
        emptyWorksheet,
        EXCEL_LABELS.DEFAULT_SHEET_NAME
      )
      return { workbook: emptyWorkbook, success: false }
    }

    // ワークブックを作成
    const workbook = xlsx.utils.book_new()

    // ワークシートデータを構築
    const worksheetData = buildWorksheetData(data)

    // ワークシートを作成
    const worksheet = xlsx.utils.aoa_to_sheet(worksheetData)

    // 列幅を設定（列設定から自動生成）
    const columnWidths = COLUMN_CONFIG.map((col) => ({ wch: col.width }))
    Object.assign(worksheet, { '!cols': columnWidths })

    // スタイルを適用
    applyWorksheetStyles(worksheet, worksheetData)

    // ワークシートをワークブックに追加
    xlsx.utils.book_append_sheet(
      workbook,
      worksheet,
      EXCEL_LABELS.DEFAULT_SHEET_NAME
    )

    return { workbook, success: true }
  } catch (error) {
    // xlsx操作エラーやその他の予期しないエラーの場合も空のワークブックを返す
    const emptyWorkbook = xlsx.utils.book_new()
    const emptyWorksheet = xlsx.utils.aoa_to_sheet([[]])
    xlsx.utils.book_append_sheet(
      emptyWorkbook,
      emptyWorksheet,
      EXCEL_LABELS.DEFAULT_SHEET_NAME
    )
    return { workbook: emptyWorkbook, success: false }
  }
}
